import { ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import classNames from 'classnames';
import { t } from 'i18n-js';
import { ThunkAction } from 'redux-thunk';

import { fetchLegalDataIfNeeded } from '@lootboy/lib/actions/LegalDataActions';
import { TStore } from '@lootboy/lib/types/store';
import { NOOP } from '@lootboy/lib/utils';

import { CircleLoader } from '~/components/atoms/CircleLoader';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import Markdown from '~/components/Markdown';
import { MenuViewHeader } from '~/components/MenuViewHeader';

import { useClosePopup } from '~/hooks/useClosePopup';
import { legalDataSelectorWithInvalidate } from '~/redux/selectors';
import { useAppDispatch } from '~/redux/store/useAppDispatch';

import markdownStyles from '~/sass/typography.module.scss';

import styles from './LegalPopupTemplate.module.scss';

export type TLegalContentType =
  | 'legal'
  | 'privacy'
  | 'terms'
  | 'withdrawal'
  | 'termsUpdate';

export type TLegalPopupTemplate = {
  type: TLegalContentType;
  children?: ReactNode;
  onClose?: () => void;
  // redux store not typed
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selector?: () => any;
  fetchAction?: () => ThunkAction<
    Promise<unknown>,
    TStore,
    undefined,
    AnyAction
  >;
};

const legalDataMap: Record<
  TLegalContentType,
  { translation: string; legalDataKey: string }
> = {
  legal: { translation: 'Footer_label_legal', legalDataKey: 'legal' },
  terms: { translation: 'Footer_label_terms', legalDataKey: 'terms' },
  privacy: { translation: 'Footer_label_privacy', legalDataKey: 'privacy' },
  withdrawal: {
    translation: 'Footer_label_withdrawal',
    legalDataKey: 'withdrawal',
  },
  termsUpdate: {
    translation: 'GDPR_popup_update_headline',
    legalDataKey: 'terms',
  },
};

const defaultLegalFetchAction = () =>
  fetchLegalDataIfNeeded({ platform: 'web' });

export function LegalPopupTemplate({
  type,
  children,
  onClose,
  selector = legalDataSelectorWithInvalidate,
  fetchAction = defaultLegalFetchAction,
}: TLegalPopupTemplate): JSX.Element {
  const dispatch = useAppDispatch();
  const closePopup = useClosePopup();
  const {
    [legalDataMap[type].legalDataKey]: legalContent,
    loading,
    didInvalidate,
  } = useSelector(selector);

  useEffect(() => {
    dispatch(fetchAction()).catch(NOOP);
  }, [dispatch, didInvalidate, fetchAction]);

  return (
    <>
      <MenuViewHeader
        title={t('web_footer_legals')}
        onClose={onClose ?? closePopup}
      />
      <ScrollContainer>
        <div
          className={classNames(
            markdownStyles.withTypography,
            styles.headlineContainer,
          )}
        >
          <h2 className={styles.heading}>
            {t(legalDataMap[type].translation)}
          </h2>
        </div>
        <section className={styles.legalsContainer}>
          {loading ? (
            <CircleLoader
              size={54}
              className={styles.loader}
            />
          ) : (
            <>
              <Markdown
                className={classNames(
                  markdownStyles.withTypography,
                  styles.legals,
                )}
              >
                {legalContent}
              </Markdown>
              {!!children && (
                <Markdown
                  className={classNames(
                    markdownStyles.withTypography,
                    styles.legals,
                  )}
                >
                  {children}
                </Markdown>
              )}
            </>
          )}
        </section>
      </ScrollContainer>
    </>
  );
}
